"use client"

import { useApprove, useIsApproved } from "@/hooks/use-approvals"
import {
  SmartField,
  SmartFormik,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@abraxas/ui/ui"
import { Web3Button } from "@abraxas/ui/web3"
import { config } from "@repo/loomlock/client"
import { Form, useFormikContext } from "formik"
import React from "react"
import { useAccount } from "wagmi"
import { useCreateListing } from "./hooks"
import { MARKETPLACE_CHAIN_ID } from "./marketplace-chain-id"
import type { IProduct, ProductType } from "@repo/loomlock/model-types"
import { QuantityInput } from "@/shared/components/QuantityInput"
import type { CreateListingFormValues } from "./hooks/useCreateListing"
import { useProductsBalance } from "@/shared/indexer/use-indexer-products"

interface CreateListingModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  // For individual token listing
  nft?: IProduct
}

const QuantityField: React.FC<{
  max: number
  disabled?: boolean
}> = ({ max, disabled }) => {
  const { values, setFieldValue } = useFormikContext<CreateListingFormValues>()

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">Quantity</label>
      <QuantityInput
        value={values.quantity}
        onChange={(value) => setFieldValue("quantity", value)}
        max={max}
        disabled={disabled}
      />
      <div className="text-xs text-gray-500">Available: {max}</div>
    </div>
  )
}

export const CreateListingModal: React.FC<CreateListingModalProps> = ({
  open,
  onClose,
  onSuccess,
  nft,
}) => {
  const { address } = useAccount()
  const { productType } = nft || {}

  const { createListing, initialValues, validationSchema, isPending } =
    useCreateListing({
      tokenId: nft?.tokenId.toString() || "0",
      productType: productType as ProductType,
    })

  const { getBalance } = useProductsBalance({
    address: address || "",
    productType: productType,
    chainIds: [MARKETPLACE_CHAIN_ID],
  })

  const nftContractAddress = config[MARKETPLACE_CHAIN_ID]?.contracts[
    productType!
  ] as `0x${string}`

  const marketplaceAddress = config[MARKETPLACE_CHAIN_ID]?.contracts
    .marketplace as `0x${string}`

  // Approval hooks
  const { isApproved, refetch: refetchApproval } = useIsApproved({
    account: address,
    operator: marketplaceAddress,
    contract: nftContractAddress,
    chainId: MARKETPLACE_CHAIN_ID,
  })

  const { approve } = useApprove()

  const handleApprove = async () => {
    await approve(nftContractAddress, marketplaceAddress)
    refetchApproval()
  }

  const handleSubmit = async (values: typeof initialValues) => {
    await createListing(values)

    onSuccess?.()
  }

  const handleCancel = () => {
    onClose()
  }

  const selectedNFTDisplay = nft
    ? {
        name: (nft as any).metadata?.name || `${productType} #${nft.tokenId}`,
        image: (nft as any).metadata?.image,
      }
    : null

  const isOnMarketplaceChain = nft ? nft.chainId === MARKETPLACE_CHAIN_ID : true

  const userAddress = address?.toLowerCase()
  const availableQuantity = isPending
    ? undefined
    : userAddress
      ? getBalance(productType!, nft?.tokenId.toString()!, MARKETPLACE_CHAIN_ID)
          ?.balance
      : 0

  const maxQuantity = Math.max(1, Number(availableQuantity))

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{"List NFT"}</DialogTitle>
        </DialogHeader>
        <SmartFormik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {() => (
            <Form>
              <div className="space-y-4">
                {selectedNFTDisplay && (
                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    {selectedNFTDisplay.image && (
                      <img
                        src={selectedNFTDisplay.image}
                        alt={selectedNFTDisplay.name}
                        className="w-16 h-16 rounded object-cover"
                      />
                    )}
                    <div>
                      <div className="font-semibold">
                        {selectedNFTDisplay.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        Token ID: {nft?.tokenId}
                      </div>
                      <div className="text-sm text-gray-500">
                        productType: {productType?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                )}

                {!isOnMarketplaceChain && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="text-orange-600 mt-0.5">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-orange-800 mb-1">
                          Bridge Required
                        </div>
                        <div className="text-sm text-orange-700">
                          This item must be bridged to the marketplace chain
                          (Chain ID: {MARKETPLACE_CHAIN_ID}) before it can be
                          listed. Please bridge your item first and then return
                          to create a listing.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                <SmartField
                  className="text-black"
                  name="price"
                  label="Price per NFT (ETH)"
                  disabled={!isOnMarketplaceChain}
                />

                <QuantityField
                  max={maxQuantity}
                  disabled={!isOnMarketplaceChain}
                />

                <SmartField
                  className="text-black"
                  name="endTime"
                  label="Listing End Time"
                  widget="date"
                  disabled={!isOnMarketplaceChain}
                />

                {!isApproved && nftContractAddress && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-semibold text-yellow-800 mb-1">
                          Approval Required
                        </div>
                        <div className="text-sm text-yellow-700">
                          You need to approve the direct listings contract to
                          transfer this NFT before you can list it.
                        </div>
                      </div>
                      <Web3Button
                        size="small"
                        onClick={handleApprove}
                        chainId={MARKETPLACE_CHAIN_ID}
                        htmlType="button"
                      >
                        Approve
                      </Web3Button>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-2">
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Web3Button
                    htmlType="submit"
                    loading={isPending}
                    disabled={!isApproved || !isOnMarketplaceChain}
                    className="bg-yellow-500 hover:bg-yellow-600"
                    chainId={MARKETPLACE_CHAIN_ID}
                  >
                    {!isOnMarketplaceChain
                      ? "Bridge Required"
                      : !isApproved
                        ? "Approve First"
                        : isPending
                          ? "Creating..."
                          : "Create Listing"}
                  </Web3Button>
                </div>
              </div>
            </Form>
          )}
        </SmartFormik>
      </DialogContent>
    </Dialog>
  )
}
